import { Radio as BaseRadio } from '@base-ui/react/radio'
import React from 'react'
import styled, { css } from 'styled-components'
import { useRadioGroupVariant } from 'sutra/components/Radio/radio-group-variant'
import { accents, brand, gray, semantic } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { typography } from 'sutra/tokens/typography'

export interface RadioProps {
  value: string | number
  disabled?: boolean
  // Native tooltip, e.g. to say why a radio is disabled.
  title?: string
  // Names the radio when its children aren't text (an icon-only segment).
  'aria-label'?: string
  className?: string
  children?: React.ReactNode
}

const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

const Label = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.text.sm.fontSize}px;
  line-height: ${typography.text.sm.lineHeight}px;
  color: ${semantic.fg};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`

const Control = styled(BaseRadio.Root)`
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  /* Centers the 18px control on the first 20px line of the label. */
  margin-top: 1px;
  padding: 0;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px solid #d5d7da;
  background: ${brand.white};
  cursor: inherit;
  transition:
    background ${motion.duration.fast}ms ${motion.ease},
    border-color ${motion.duration.fast}ms ${motion.ease};

  &[data-checked] {
    border-color: ${accents.reflectionBlue[500]};
    background: ${accents.reflectionBlue[500]};
  }

  ${focusRing}
`

const Dot = styled(BaseRadio.Indicator)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${brand.white};
`

const Segment = styled(BaseRadio.Root)`
  box-sizing: border-box;
  flex: 1 1 auto;
  min-height: 32px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  font-family: ${typography.fontFamily};
  font-size: 13px;
  font-weight: ${typography.weights.medium};
  color: ${semantic.fgMuted};
  background: ${brand.white};
  cursor: pointer;
  transition:
    background ${motion.duration.fast}ms ${motion.ease},
    color ${motion.duration.fast}ms ${motion.ease};

  /* The hidden input sits between segments, so the first segment is the
     group's first child and every later one gets a divider. */
  &:not(:first-child) {
    border-left: 1px solid ${gray[300]};
  }

  &:hover:not([data-checked], [data-disabled]) {
    background: ${gray[100]};
    color: ${semantic.fg};
  }

  &[data-checked] {
    background: ${accents.reflectionBlue[500]};
    color: ${brand.white};
  }

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: -2px;
  }
`

// Must sit inside a `RadioGroup`: Base UI tracks the selection there, and a
// radio outside one is never checked.
export const Radio = ({
  value,
  disabled,
  title,
  'aria-label': ariaLabel,
  className,
  children,
}: RadioProps): React.ReactElement => {
  const variant = useRadioGroupVariant()

  if (variant === 'button') {
    return (
      <Segment
        value={value}
        disabled={disabled}
        title={title}
        aria-label={ariaLabel}
        className={className}
      >
        {children}
      </Segment>
    )
  }

  return (
    <Label $disabled={disabled === true} title={title} className={className}>
      <Control value={value} disabled={disabled} aria-label={ariaLabel}>
        <Dot />
      </Control>
      {children === undefined ? null : <span>{children}</span>}
    </Label>
  )
}

Radio.displayName = 'Radio'
